import { SchoolMoodQuery, SchoolMoodQueryVariables } from '@/graphql/generated/graphql';
import { SCHOOL_MOOD } from '@/graphql/queries/report/ga-interactions';
import useTranslation from '@/hooks/i18n';
import { useQuery } from '@apollo/client';
import { ChartOptions } from 'chart.js';
import { useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useDashboardStore } from '../../store/useDashboardStore';
import { getColor } from '../../utils/colors';
import { useAdminStore } from '@/pages/admin/store/useAdmin.store';

interface DataType {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
  }[];
}

export default function SchoolMoodComponent() {
  const { page } = useAdminStore();

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          title: function () {
            return '';
          },
          label: function (tooltipItem) {
            return tooltipItem.label || '';
          },
        },
      },
    },
  };

  const [data, setData] = useState<DataType>({
    labels: [],
    datasets: [
      {
        label: '',
        data: [],
        backgroundColor: [],
      },
    ],
  });

  const { t } = useTranslation();
  const { getDateRange, schoolsIds } = useDashboardStore();
  const { data: response, loading } = useQuery<SchoolMoodQuery, SchoolMoodQueryVariables>(SCHOOL_MOOD, {
    variables: {
      startDate: getDateRange()?.startDate,
      endDate: getDateRange()?.endDate,
      schoolsIds,
    },
  });

  useEffect(() => {
    if (response && response.getSchoolMood) {
      const features = response.getSchoolMood
        .map((mood) => mood.emotions)
        .flatMap((emotions) => emotions)
        .reduce((obj: { [key: string]: number }, current) => {
          if (obj[current!.emotion!]) {
            obj[current!.emotion!] += current?.count || 0;
          } else {
            obj[current!.emotion!] = current?.count || 0;
          }
          return obj;
        }, {});

      // Convert features object to an array of entries and sort by values
      const sortedEntries = Object.entries(features).sort(([, a], [, b]) => b - a);

      // Map sorted entries to labels and data
      const sortedLabels = sortedEntries.map(([emotion]) => t(emotion));
      const sortedData = sortedEntries.map(([, count]) => count);

      setData({
        labels: sortedLabels,
        datasets: [
          {
            data: sortedData,
            backgroundColor: sortedEntries.map((_, index) => getColor(index)),
            label: '',
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center">
          <div className="size-64 animate-pulse rounded-full bg-gray-300"></div>
        </div>
      );
    }
    if (data.labels.length === 0) {
      return (
        <div className="flex items-center justify-center">
          {/* //Empty state of a doughnut chart */}
          <div className="flex flex-col items-center justify-center">
            <p className="text-sh-xs text-gray-500">No data available</p>
          </div>
        </div>
      );
    }
    return <Doughnut data={data} options={options} style={{ width: 'auto' }} />;
  };
  return (
    <>
      <div className="border-b-2 border-[#ECECEC] px-11 py-6">
        <p className=" text-sh-xs font-bold">{page?.dashboard?.chartsTitles?.schoolMood || 'School Mood'}</p>
      </div>
      <div className=" p-5">{renderContent()}</div>
    </>
  );
}
